import { Component, Element, h, Host, Prop, Event, EventEmitter, Method, State, Listen } from '@stencil/core';

const URL_YOUTUBE_FRAME_API = 'https://www.youtube.com/iframe_api';

const getVideoIdFromUrl = url => {
    const regExpVideoId = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExpVideoId);
    return match && match[7].length == 11 ? match[7] : false;
};

@Component({
    tag: 'sew-video',
    styleUrl: 'sew-video.scss',
    shadow: true,
})
export class SewVideo {
    @Element() host: HTMLSewVideoElement;

    @Prop({ attribute: 'video-src' }) src: string;
    @Prop({ attribute: 'video-title' }) videoTitle: string;
    @Prop({ attribute: 'allow-full-screen' }) allowFullScreen = true;
    @Prop() caption: string;
    @Prop() shiftCaptionRight = false;
    @Prop() autoplay: boolean;
    @Prop() mute: boolean | string;
    @Prop() loop: boolean;
    @Prop() controls: boolean;
    @Prop() disablekb: boolean;
    @Prop() playsinline: boolean;
    @Prop() showinfo: boolean;
    @Prop() modestbranding: boolean;
    @Prop() fs: boolean;
    @Prop() width = '100%';
    @Prop() height = '100%';
    @Prop() aspectRatio: string;
    @Prop() isReady = false;
    @Prop() consentButtonText = 'Activate video';
    @Prop() disableConsentCheck = false;
    @Prop() preview = false;
    @Prop() hideCaption = false;

    @State() consent = false;
    @State() posterEl: HTMLSewPictureElement;

    @Event() videoEnded: EventEmitter<boolean>;
    @Event() videoPlaying: EventEmitter<boolean>;
    @Event() videoPaused: EventEmitter<boolean>;
    @Event() playerReady: EventEmitter<boolean>;
    @Event() youtubeapiready: EventEmitter<boolean>;
    @Event() consentSet: EventEmitter<boolean>;

    private video: HTMLDivElement;
    private initialized = false;

    @Method()
    public async startVideo(): Promise<void> {
        this.initialized && this.YTPlayer.playVideo();
        if (this.posterEl) {
            this.posterEl.remove();
            this.posterEl = null;
        }
    }

    @Method()
    public async stopVideo(): Promise<void> {
        this.initialized && this.YTPlayer.stopVideo();
    }

    @Method()
    public async pauseVideo(): Promise<void> {
        this.initialized && this.YTPlayer.pauseVideo();
    }

    @Method()
    public async resetVideo(): Promise<void> {
        this.initialized && this.YTPlayer.seekTo(0);
        this.stopVideo();
    }

    @Listen('consentSet', { target: 'window' })
    triggerBootstrap(): void {
        if (!this.initialized) this.bootstrapVideo();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private YTPlayer: any = {};

    private getConsent(): boolean {
        return document.cookie.indexOf('youtube-opt-in=true') !== -1 || this.disableConsentCheck;
    }

    private setConsent = (): void => {
        document.cookie = 'youtube-opt-in=true;path=/';
        globalThis.__cmp && globalThis.__cmp('setVendorConsent', ['s30', 1]);
        this.consent = true;
        this.consentSet.emit(true);
    };

    componentDidLoad(): void {
        if (this.mute === 'false' || this.mute === '0') {
            this.mute = false;
        } else if (this.mute === '' || this.mute === 'true' || this.mute === '1') {
            this.mute = true;
        }

        this.posterEl = this.host.querySelector('sew-picture');
        this.bootstrapVideo();
    }

    private bootstrapVideo() {
        this.consent = this.getConsent();

        if (!this.consent) return;

        this.initialized = true;

        if (window['YT']) {
            this.initVideo();
        } else {
            const tag = document.createElement('script');
            tag.src = URL_YOUTUBE_FRAME_API;
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

            window.addEventListener('youtubeapiready', () => {
                this.initVideo();
            });

            window['onYouTubeIframeAPIReady'] = () => {
                this.youtubeapiready.emit(true);
            };
        }
    }

    private initVideo = (): void => {
        this.YTPlayer = new window['YT'].Player(this.video, {
            height: this.height,
            width: this.width,
            videoId: getVideoIdFromUrl(this.src),
            playerVars: {
                mute: this.mute ?? (this.autoplay ? 1 : 0),
                autoplay: this.autoplay ? 1 : 0,
                controls: this.controls ? 1 : 0,
                disablekb: this.disablekb ? 1 : 0,
                showinfo: this.showinfo ? 1 : 0,
                fs: this.fs ? 1 : 0,
                modestbranding: this.modestbranding ? 1 : 0,
                playsinline: this.playsinline ? 1 : 0,
                rel: 0,
            },
            allowFullScreen: this.allowFullScreen,
            events: {
                onReady: this.onPlayerReady,
                onStateChange: this.onPlayerStateChange,
            },
        });
    };

    private onPlayerReady = (): void => {
        this.posterEl?.addEventListener(
            'click',
            () => {
                this.startVideo();
            },
            { once: true }
        );
        this.playerReady.emit(true);
        this.isReady = true;

        if (this.preview) {
            this.host.addEventListener('mouseenter', () => {
                this.startVideo();
            });
            this.host.addEventListener('mouseleave', () => {
                this.resetVideo();
            });
        }
    };

    private onPlayerStateChange = event => {
        if (event.data == window['YT'].PlayerState.ENDED) {
            this.videoEnded.emit(true);

            if (this.loop) {
                this.startVideo();
            }
        } else if (event.data == window['YT'].PlayerState.PLAYING) {
            this.videoPlaying.emit(true);
            this.pauseOtherVideos();
        } else if (event.data == window['YT'].PlayerState.PAUSED) {
            this.videoPaused.emit(true);
        }
    };

    private pauseOtherVideos = (): void => {
        const videos = document.querySelectorAll('sew-video');
        videos.forEach(video => {
            if (video !== this.host && !video.autoplay) {
                video.pauseVideo();
            }
        });
    };

    public render() {
        return (
            <Host>
                <div
                    style={{ 'aspect-ratio': this.aspectRatio }}
                    class={{
                        'video-wrapper': true,
                        'no-consent': !this.consent,
                        'has-poster': !!this.posterEl,
                    }}
                >
                    <div ref={el => (this.video = el)} />
                    {(!this.autoplay || !this.consent) && <slot name="poster"></slot>}
                    {this.consent && !this.autoplay && this.posterEl && (
                        <div class="video">
                            <sew-icon icon="play-filled" size="xl"></sew-icon>
                        </div>
                    )}
                    {!this.consent && (
                        <div class="consent">
                            <slot></slot>
                            <sew-ws-button hierarchy="filled">
                                <button onClick={this.setConsent}>{this.consentButtonText}</button>
                            </sew-ws-button>
                        </div>
                    )}
                </div>
                {!this.hideCaption && this.caption && (
                    <p class={{ 'caption': true, 'shift-caption-right': this.shiftCaptionRight }}>
                        {this.caption}
                    </p>
                )}
            </Host>
        );
    }
}
